import { Link, useLocation, useNavigate } from "react-router-dom";
import { FormEvent, useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useProfessionalAuth } from "../../store/authProfessionalStore";
import { RequiredMark } from "../../components/RequiredMark";

const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const PENDING_LOGIN_EMAIL_KEY = "agbaza.pro.login.pendingEmail";

export default function ProLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useProfessionalAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isEmail = useMemo(() => emailRegex.test(email.trim()), [email]);
  const canSubmit = isEmail && password.length > 0 && !submitting;

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setError("");
    setSubmitting(true);
    const result = await signIn({ email: email.trim(), password });
    setSubmitting(false);

    if (!result.ok) {
      setError(result.message || "Identifiants incorrects");
      return;
    }

    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    sessionStorage.setItem(PENDING_LOGIN_EMAIL_KEY, email.trim());
    navigate("/professionnel/connexion/code", {
      replace: true,
      state: {
        email: email.trim(),
        redirect: redirect ?? undefined,
        mode: "login",
      },
    });
  };

  return (
    <div className="relative h-screen overflow-hidden">
      <img
        src={import.meta.env.BASE_URL + 'maps-reset-side.jpg'}
        alt="pro-login"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30" />

      <div className="absolute inset-0 z-10 px-4 md:px-8 py-6 md:py-8 flex">
        <div className="max-w-6xl mx-auto flex flex-col w-full">
          <div className="pl-2 md:pl-6 pt-2 md:pt-6 max-w-2xl">
            <h1 className="text-white text-2xl md:text-3xl font-semibold leading-snug">
              Le meilleur moyen et le plus simple de vous tenir informé sur vos clients .
            </h1>
          </div>

          <div className="mt-auto flex items-end justify-between gap-6 pl-2 md:pl-6 pr-4 md:pr-8 pb-4 md:pb-8">
            <div className="w-full max-w-xl bg-white/95 backdrop-blur rounded-[12px] shadow p-6 md:p-7">
              <form className="space-y-4" onSubmit={onSubmit}>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-[10px] px-3 py-2">
                    {error}
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-base" style={{ color: '#0B1F16' }}>
                    Email
                    <RequiredMark />
                  </label>
                  <input
                    name="email"
                    type="email"
                    placeholder="exemple@domaine.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value.replace(/\s/g, ""))}
                    className={`w-full h-14 rounded-[10px] border-2 px-3 text-base ${isEmail || email === '' ? '' : 'border-red-400'}` }
                    style={{ borderColor: isEmail || email === '' ? '#D1E2DD' : '#F87171' }}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-base" style={{ color: '#0B1F16' }}>
                      Mots de passe
                      <RequiredMark />
                    </label>
                    <Link to="/professionnel/mot-de-passe/oubli" className="text-sm font-semibold" style={{ color: '#00553E' }}>
                      Mots de passe oublié ?
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full h-14 rounded-[10px] border-2 px-3 pr-12 text-base"
                      style={{ borderColor: '#D1E2DD' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-[#0B1F16] focus:outline-none focus:ring-2 focus:ring-[#00553E]/30"
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    >
                      {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-700">
                  En cliquant sur le bouton, vous acceptez nos <u>conditions d’utilisation</u> et notre <u>politique de confidentialité</u>.
                </p>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full h-14 rounded-[10px] font-semibold cursor-pointer disabled:opacity-60"
                  style={{ backgroundColor: '#00553E', color: '#FFFFFF' }}
                >
                  {submitting ? "Connexion..." : "Connectez - vous"}
                </button>
                <div className="text-sm text-[#0B1F16]">
                  Vous n’avez pas encore de compte ?{" "}
                  <Link className="font-semibold" style={{ color: '#00553E' }} to="/professionnel/inscription/informations-1">
                    S’enregistrer
                  </Link>
                </div>
              </form>
            </div>
            <div className="hidden md:block shrink-0" style={{ marginRight: 0 }}>
              <img src={import.meta.env.BASE_URL + 'Group 5.png'} alt="agabaza-logo" className="w-auto" style={{ height: 144 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

